import type React from 'react'
import { useCallback, useState } from 'react'
import { useEscapeKey } from '../../app/hooks/useEscapeKey'
import type { Phase } from './useQuestionFlow'

/**
 * useExitGuards — exit-confirmation + Escape-key handling for a practice
 * session.
 *
 * Extracted from PracticeSession.tsx as part of the F-FE-1 god-component
 * split (K12: custom hooks, not mini-components).
 *
 * F-QA-1 / F-UX-NEW-1 (PRESERVED): the confirmation modal only prompts when
 * the learner is mid-question with at least one committed answer — during
 * feedback / fluency / summary phases the previous answer is already saved,
 * so exiting directly avoids a misleading "am I about to lose my answer?"
 * dialog.
 *
 * Escape closes the confirm modal if open, otherwise routes through the same
 * exit request. The listener is disabled while the cheat-sheet is open so it
 * doesn't steal the drawer's own Escape.
 *
 * HOOKS-ORDER SAFETY: called unconditionally at the top of PracticeSession.
 * The unmount timer cleanup for session timers lives inside useQuestionFlow
 * and useSessionTimer (each owns its own refs), so this hook is purely the
 * exit/escape concern.
 */
interface UseExitGuardsParams {
  phase: Phase
  answeredCount: number
  isCheatSheetOpen: boolean
  isSkipConfirmOpen: boolean
  onDismissSkipConfirm: () => void
  onExit: () => void
}

export interface ExitGuards {
  showExitConfirm: boolean
  setShowExitConfirm: React.Dispatch<React.SetStateAction<boolean>>
  requestExit: () => void
}

export function useExitGuards({
  phase,
  answeredCount,
  isCheatSheetOpen,
  isSkipConfirmOpen,
  onDismissSkipConfirm,
  onExit,
}: UseExitGuardsParams): ExitGuards {
  const [showExitConfirm, setShowExitConfirm] = useState(false)

  const requestExit = useCallback(() => {
    if (phase === 'question' && answeredCount > 0) {
      setShowExitConfirm(true)
    } else {
      onExit()
    }
  }, [phase, answeredCount, onExit])

  const handleEscapePress = useCallback(() => {
    if (isSkipConfirmOpen) {
      onDismissSkipConfirm()
      return
    }
    if (showExitConfirm) {
      setShowExitConfirm(false)
      return
    }
    requestExit()
  }, [isSkipConfirmOpen, onDismissSkipConfirm, requestExit, showExitConfirm])

  useEscapeKey(handleEscapePress, !isCheatSheetOpen)

  return {
    showExitConfirm,
    setShowExitConfirm,
    requestExit,
  }
}
